'use client';

// ── Maximum Payout Request · "$4,000 profit" example ─────────────────────────
// Figma "Moldura · Exemplo · pedido máximo": desktop 12071:60003 (792×316),
// mobile 12471:10410 (366×820). Four calculation steps (a row on desktop, a
// stack with down arrows on mobile) and the paid-out / buffer split bar.
// Every number is an illustrative example; sizes are Figma px / 8 in rem.

import { ArrowRightIcon } from '../Icons';
import { ExampleLabel, FigureFrame } from '../primitives';

const TEXT = 'mb-0 text-[2rem] leading-[3rem]';

function Step({
  label,
  value,
  note,
  tone = 'gray',
  dataUi,
}: {
  label: string;
  value: string;
  note: string;
  tone?: 'gray' | 'green';
  dataUi: string;
}) {
  const green = tone === 'green';
  return (
    <div
      data-ui={dataUi}
      className={`flex w-full flex-col gap-[0.5rem] rounded-[0.25rem] p-[1.75rem] lg:min-w-0 lg:flex-1 lg:self-stretch ${
        green ? 'bg-[#e2f3ef]' : 'bg-[#f2f4f3]'
      }`}
    >
      <p className={`${TEXT} whitespace-nowrap font-normal ${green ? 'text-[#196551]/[0.64]' : 'text-black/[0.64]'}`}>
        {label}
      </p>
      <p className={`mb-0 whitespace-nowrap text-[3rem] font-medium leading-[4rem] ${green ? 'text-[#228e71]' : 'text-black'}`}>
        {value}
      </p>
      <p className={`${TEXT} font-normal text-black/[0.64]`}>{note}</p>
    </div>
  );
}

function Arrow() {
  return (
    <div data-ui="figure.maximumPayoutRequest.arrow" className="flex h-[2rem] w-[2rem] shrink-0 items-center justify-center text-black/[0.32]">
      <ArrowRightIcon className="h-[2rem] w-[2rem] rotate-90 lg:rotate-0" />
    </div>
  );
}

export default function MaximumPayoutRequest() {
  return (
    <FigureFrame data-ui="figure.maximumPayoutRequest" className="gap-[2.5rem] font-geist">
      <ExampleLabel>EXAMPLE</ExampleLabel>

      {/* Passos de cálculo */}
      <div
        data-ui="figure.maximumPayoutRequest.steps"
        className="flex w-full flex-col items-center gap-[1.5rem] lg:flex-row lg:items-center"
      >
        <Step dataUi="figure.maximumPayoutRequest.step-1" label="Profit at request" value="$4,000" note="total profit in the account" />
        <Arrow />
        <Step dataUi="figure.maximumPayoutRequest.step-2" label="50% of profit" value="$2,000" note="the most you could take" />
        <Arrow />
        <Step dataUi="figure.maximumPayoutRequest.step-3" label="Payout cap" value="$1,500" note="applies to each account" />
        <Arrow />
        <Step dataUi="figure.maximumPayoutRequest.step-4" label="Your payout" value="$1,500" note="the lower of both" tone="green" />
      </div>

      {/* Divisão do lucro — desktop: bar then axis; mobile: axis, bar, labels below */}
      <div data-ui="figure.maximumPayoutRequest.split" className="flex w-full flex-col gap-[1rem]">
        <div className="order-1 flex w-full items-start justify-between whitespace-nowrap lg:order-2">
          <p className={`${TEXT} font-normal text-black/40`}>$0</p>
          <p className={`${TEXT} font-normal text-black/40`}>total profit $4,000</p>
        </div>
        <div
          data-ui="figure.maximumPayoutRequest.bar"
          className="relative order-2 h-[11.5rem] w-full lg:order-1 lg:h-[4rem] lg:overflow-hidden lg:rounded-[0.25rem] lg:bg-[#e2f3ef]"
        >
          <div className="absolute left-0 top-0 h-[4rem] w-full rounded-[0.25rem] bg-[#e2f3ef] lg:hidden" />
          {/* Figma draws the paid-out share at 260.03/744 on desktop and 118.987/318 on mobile */}
          <div
            data-ui="figure.maximumPayoutRequest.bar-fill"
            className="absolute left-0 top-0 h-[4rem] w-[37.42%] rounded-[0.25rem] bg-[#228e71] lg:w-[34.95%]"
          >
            <p className={`${TEXT} absolute left-[0.375rem] top-[0.75rem] hidden whitespace-nowrap font-medium text-white lg:block`}>
              Paid out $1,500
            </p>
          </div>
          <p
            className={`${TEXT} absolute left-[0.39rem] top-[5rem] whitespace-nowrap font-medium text-[#228e71] lg:hidden`}
          >
            Paid out $1,500
          </p>
          <p
            className={`${TEXT} absolute left-[0.96rem] top-[8.5rem] whitespace-nowrap font-medium text-[#196551] lg:left-[calc(34.95%+1.5rem)] lg:top-[0.75rem]`}
          >
            Stays in the account as buffer · $2,500
          </p>
        </div>
      </div>
    </FigureFrame>
  );
}
